/**
 * shell/faq-page.ts
 *
 * Progressive enhancement controller for the public /faq page.
 *
 * Capabilities (all degrade gracefully without JS):
 *   - Live search filter over questions and answers (80 ms debounce).
 *   - Category chip filter that scopes the accordion in place.
 *   - Per-question copy-link button → writes to clipboard, shows toast.
 *   - Helpful / Not-helpful feedback button → POSTs to /api/faq/feedback
 *     with the anti-forgery token, then shows a confirmation toast only
 *     after the network call resolves (so the toast tells the truth).
 *   - Deep-link from spotlight cards: opens the target <details> and scrolls.
 *   - Hash-based open-on-load (`/faq#q-do-i-need-to-install`).
 *   - Honours `prefers-reduced-motion` for smooth scroll.
 *
 * Imports use `.js` even for `.ts` source files (per AGENTS §10).
 */
import { byId, query, queryAll } from '../types/dom.js';
// ---------------------------------------------------------------------------
// i18n bridge
// ---------------------------------------------------------------------------
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
// Search-input debounce. Standardized to 100 ms across every Parsepad
// search surface (home, /tools, FAQ, contact-faq, command palette) so the
// perceived responsiveness is identical no matter where the user types.
const SEARCH_DEBOUNCE_MS = 100;
const TOAST_VISIBLE_MS = 1500;
const SPOTLIGHT_HIGHLIGHT_MS = 1200;
const ALL_CATEGORY = 'all';
const FEEDBACK_ENDPOINT = '/api/faq/feedback';
const FEEDBACK_TOAST_OK = t('js.faq.feedbackRecorded', 'Thanks \u2014 feedback recorded');
const FEEDBACK_TOAST_FAIL = t('js.faq.feedbackFailed', "Couldn't record \u2014 try again");
const COPY_LINK_TOAST_OK = t('js.faq.linkCopied', 'Link copied');
const COPY_LINK_TOAST_FAIL = t('js.faq.copyFailed', 'Could not copy link');
const COPY_LINK_ANNOUNCEMENT_OK = t('js.faq.linkCopiedAnnouncement', 'Copied link to clipboard');
const COPY_LINK_ANNOUNCEMENT_FAIL = t('js.faq.copyFailedAnnouncement', 'Could not copy link to clipboard');
const COPY_STATUS_ID = 'faq-copy-status';
/**
 * Push `message` into the sr-only `aria-live="polite"` announcer rendered
 * by `Faq.cshtml`. Clears the node first so consecutive identical copies
 * still trigger a re-announcement on screen readers that diff textContent.
 *
 * This is the page's only announcer, and deliberately so: it is rendered on
 * every load, it is never hidden, and it holds nothing but text. A region that
 * is revealed from `hidden` with its content already in place — which is what
 * the visual empty state below the accordion is — enters the accessibility tree
 * fully formed and is therefore not reported as a change at all.
 */
function announceStatus(message) {
    if (message.length === 0)
        return;
    const node = byId(COPY_STATUS_ID);
    if (node === null)
        return;
    node.textContent = '';
    // Force the assistive tech to pick up the mutation on the next tick
    // even when the same message fires twice in a row.
    window.setTimeout(() => {
        node.textContent = message;
    }, 50);
}
/** Look up the ASP.NET anti-forgery token rendered into `#faq-antiforgery`. */
function readCsrfToken() {
    const scoped = query('#faq-antiforgery input[name="__RequestVerificationToken"]');
    if (scoped !== null && scoped.value.length > 0) {
        return scoped.value;
    }
    const fallback = query('input[name="__RequestVerificationToken"]');
    return fallback?.value ?? '';
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function debounce(fn, wait) {
    let timer = null;
    return (...args) => {
        if (timer !== null)
            window.clearTimeout(timer);
        timer = window.setTimeout(() => {
            timer = null;
            fn(...args);
        }, wait);
    };
}
function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
function applyFilters(els, queryText, category) {
    const q = queryText.trim().toLowerCase();
    let visibleCount = 0;
    for (const item of els.items) {
        const haystack = item.dataset['search'] ?? '';
        const itemCategory = item.dataset['faqCategory'] ?? '';
        const matchesQuery = q.length === 0 || haystack.includes(q);
        const matchesCategory = category === ALL_CATEGORY || itemCategory === category;
        const visible = matchesQuery && matchesCategory;
        item.hidden = !visible;
        if (visible)
            visibleCount += 1;
    }
    // Hide an entire group section when none of its items are visible.
    for (const group of els.groups) {
        const hasVisible = Array.from(group.querySelectorAll('.faq-accordion__item')).some((el) => !el.hidden);
        group.hidden = !hasVisible;
    }
    if (els.empty !== null) {
        // `hidden` is the visual toggle; the announcement is a separate concern
        // routed through the always-present announcer. Read the transition before
        // writing it, so narrowing a query that was already matching nothing does
        // not re-announce on every keystroke, and announce the empty state's own
        // visible heading rather than a second string that could drift from it.
        const wasEmpty = !els.empty.hidden;
        els.empty.hidden = visibleCount > 0;
        if (visibleCount === 0 && !wasEmpty) {
            const title = els.empty.querySelector('.faq-accordion__empty-title');
            announceStatus(title?.textContent?.trim() ?? '');
        }
    }
}
function initSearch(els, getCategory) {
    const onInput = debounce(() => {
        applyFilters(els, els.input.value, getCategory());
        if (els.clearBtn !== null) {
            els.clearBtn.hidden = els.input.value.length === 0;
        }
    }, SEARCH_DEBOUNCE_MS);
    els.input.addEventListener('input', onInput);
    const clearBtn = els.clearBtn;
    if (clearBtn !== null) {
        clearBtn.addEventListener('click', () => {
            els.input.value = '';
            clearBtn.hidden = true;
            applyFilters(els, '', getCategory());
            els.input.focus();
        });
    }
}
// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
function initCategories(els, setCategory) {
    els.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
            const next = chip.dataset['faqFilter'] ?? ALL_CATEGORY;
            els.chips.forEach((c) => {
                const isActive = c === chip;
                c.classList.toggle('is-active', isActive);
                c.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
            setCategory(next);
            applyFilters(els, els.input.value, next);
        });
    });
}
function createToast() {
    const root = query('.faq-toast');
    const text = query('.faq-toast__text');
    let hideTimer = null;
    return {
        show(message) {
            if (root === null)
                return;
            if (text !== null)
                text.textContent = message;
            root.hidden = false;
            // Force a layout flush so the transition runs.
            void root.offsetHeight;
            root.classList.add('is-visible');
            if (hideTimer !== null)
                window.clearTimeout(hideTimer);
            hideTimer = window.setTimeout(() => {
                root.classList.remove('is-visible');
                window.setTimeout(() => {
                    root.hidden = true;
                }, 220);
            }, TOAST_VISIBLE_MS);
        },
    };
}
// ---------------------------------------------------------------------------
// Copy link
// ---------------------------------------------------------------------------
function initCopyLinks(toast) {
    queryAll('[data-faq-copy]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const id = btn.dataset['faqCopy'];
            if (id === undefined || id.length === 0)
                return;
            const url = `${window.location.origin}${window.location.pathname}#q-${id}`;
            const onSuccess = () => {
                toast.show(COPY_LINK_TOAST_OK);
                announceStatus(COPY_LINK_ANNOUNCEMENT_OK);
            };
            const onFailure = () => {
                toast.show(COPY_LINK_TOAST_FAIL);
                announceStatus(COPY_LINK_ANNOUNCEMENT_FAIL);
            };
            const writePromise = navigator.clipboard?.writeText(url);
            if (writePromise !== undefined) {
                writePromise.then(onSuccess, onFailure);
            }
            else {
                // Fallback for older browsers — select + execCommand.
                const tmp = document.createElement('input');
                tmp.value = url;
                document.body.appendChild(tmp);
                tmp.select();
                try {
                    document.execCommand('copy');
                    onSuccess();
                }
                catch {
                    onFailure();
                }
                document.body.removeChild(tmp);
            }
        });
    });
}
// ---------------------------------------------------------------------------
// Helpful / Not helpful (stubbed)
// ---------------------------------------------------------------------------
function initFeedback(toast) {
    queryAll('[data-faq-feedback]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const direction = btn.dataset['faqFeedback'];
            const faqId = btn.dataset['faqId'];
            if (direction === undefined || faqId === undefined)
                return;
            const group = btn.closest('.faq-accordion__feedback');
            const peers = group === null
                ? []
                : Array.from(group.querySelectorAll('[data-faq-feedback]'));
            // Optimistically lock both buttons in the same group and mark
            // the clicked one selected. If the network call fails we roll
            // these back so the user can retry.
            peers.forEach((b) => {
                b.disabled = true;
            });
            btn.classList.add('is-selected');
            const token = readCsrfToken();
            fetch(FEEDBACK_ENDPOINT, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    RequestVerificationToken: token,
                },
                body: JSON.stringify({ faqId, helpful: direction === 'up' }),
            })
                .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }
                toast.show(FEEDBACK_TOAST_OK);
                // Both outcomes have to reach the announcer as well as the
                // toast. The toast writes its text while it is still
                // `hidden` and then reveals it with the content already in
                // place, which is the one shape a live region does not
                // report -- so on its own it told a sighted user the vote
                // landed and told everyone else nothing. The click also
                // disables both buttons, so there is no focused control
                // left to carry the outcome. The toast sentences are
                // already whole sentences, so they are reused rather than
                // paired with announcement-only copy.
                announceStatus(FEEDBACK_TOAST_OK);
            })
                .catch(() => {
                // Roll back the optimistic UI so the user can retry.
                peers.forEach((b) => {
                    b.disabled = false;
                });
                btn.classList.remove('is-selected');
                toast.show(FEEDBACK_TOAST_FAIL);
                announceStatus(FEEDBACK_TOAST_FAIL);
            });
        });
    });
}
// ---------------------------------------------------------------------------
// Spotlight deep-link + hash open
// ---------------------------------------------------------------------------
function openItemById(itemId, withHighlight) {
    const target = byId(itemId);
    if (target === null)
        return;
    target.open = true;
    target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
    });
    if (withHighlight) {
        target.classList.add('is-spotlighted');
        window.setTimeout(() => {
            target.classList.remove('is-spotlighted');
        }, SPOTLIGHT_HIGHLIGHT_MS);
    }
}
function initSpotlight() {
    queryAll('[data-spotlight-target]').forEach((card) => {
        card.addEventListener('click', (event) => {
            const targetId = card.dataset['spotlightTarget'];
            if (targetId === undefined || targetId.length === 0)
                return;
            // Allow modifier-clicks (open-in-new-tab etc.) to pass through.
            if (event.ctrlKey ||
                event.metaKey ||
                event.shiftKey ||
                event.altKey) {
                return;
            }
            event.preventDefault();
            window.history.replaceState(null, '', `#${targetId}`);
            openItemById(targetId, true);
        });
    });
}
function openFromHashOnLoad() {
    const hash = window.location.hash.slice(1);
    if (hash.length === 0)
        return;
    if (!hash.startsWith('q-'))
        return;
    // Defer one tick so the browser has settled the layout.
    window.requestAnimationFrame(() => {
        openItemById(hash, true);
    });
}
// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
function gatherElements() {
    const input = byId('faqSearch');
    const accordion = byId('faqAccordion');
    if (input === null || accordion === null)
        return null;
    return {
        input,
        clearBtn: query('.faq-search__clear'),
        accordion,
        empty: query('.faq-accordion__empty'),
        items: queryAll('.faq-accordion__item', accordion),
        groups: queryAll('.faq-group', accordion),
        chips: queryAll('[data-faq-filter]'),
    };
}
function init() {
    // Bail quietly when this module is loaded on a different page.
    const els = gatherElements();
    if (els === null)
        return;
    let activeCategory = ALL_CATEGORY;
    const getCategory = () => activeCategory;
    const setCategory = (next) => {
        activeCategory = next;
    };
    const toast = createToast();
    initSearch(els, getCategory);
    initCategories(els, setCategory);
    initCopyLinks(toast);
    initFeedback(toast);
    initSpotlight();
    // Sync the clear-button visibility on initial load (e.g. browser autofill).
    if (els.clearBtn !== null) {
        els.clearBtn.hidden = els.input.value.length === 0;
    }
    openFromHashOnLoad();
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
}
else {
    init();
}
//# sourceMappingURL=faq-page.js.map